import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted } from "vue";
  import { useRoute } from "vue-router";
  import { useToast } from "vue-toastification";
  import inclusionApi from "@/services/api/inclusionApi";
  import {
    InclusionInterface,
    InclusionPackageInterface,
    InclusionCategoryInterface,
  } from "@/types/InclusionInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminEstateInclusions',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const toast = useToast();
  const estateId = route.params.estateId as string;

  const view = ref<"list" | "detail">("list");
  const loading = ref(false);
  const packages = ref<InclusionInterface[]>([]);
  const selectedPackage = ref<InclusionInterface | null>(null);
  const localCategories = ref<InclusionCategoryInterface[]>([]);

  const bulkUploading = ref(false);
  const bulkInputRef = ref<HTMLInputElement | null>(null);

  const handleBulkUpload = async (e: Event) => {
    const input = e.target as HTMLInputElement;

    if (!input.files || !input.files.length || !selectedPackage.value) return;

    const file = input.files[0];
    const data = new FormData();

    data.append("file", file);
    bulkUploading.value = true;

    try {
      const updated = await inclusionApi.bulkUploadInclusions(
        selectedPackage.value._id,
        data
      );
      const newCategories = updated.categories ?? [];

      localCategories.value = [...newCategories];
      selectedPackage.value.categories = localCategories.value;
      const pkgIdx = packages.value.findIndex(
        (p) => p._id === selectedPackage.value!._id
      );

      if (pkgIdx !== -1)
        packages.value[pkgIdx].categories = localCategories.value;

      toast.success("Inclusions uploaded successfully");
    } catch {
      toast.error("Failed to upload inclusions. Check the file format.");
    } finally {
      bulkUploading.value = false;

      if (bulkInputRef.value) bulkInputRef.value.value = "";
    }
  };

  const showPackageModal = ref(false);
  const isEditPackageMode = ref(false);
  const savingPackage = ref(false);
  const packageForm = ref({ name: "" });
  const editingPackageId = ref<string | null>(null);

  const showCategoryModal = ref(false);
  const isEditCategoryMode = ref(false);
  const savingCategory = ref(false);
  const categoryForm = ref({ name: "" });
  const editingCategoryIndex = ref<number | null>(null);

  const showItemModal = ref(false);
  const isEditItemMode = ref(false);
  const savingItem = ref(false);
  const editingCategoryIdx = ref<number | null>(null);
  const editingItemIndex = ref<number | null>(null);
  const itemNameError = ref(false);
  const itemForm = ref<{ name: string; description: string; url: string }>({
    name: "",
    description: "",
    url: "",
  });

  const collapsedCategories = ref(new Set<number>());

  const fetchPackages = async () => {
    try {
      loading.value = true;
      packages.value = await inclusionApi.getInclusionsByEstateId(estateId);
    } catch {
      toast.error("Failed to load inclusion packages");
    } finally {
      loading.value = false;
    }
  };

  onMounted(fetchPackages);

  const getCategoryCountText = (pkg: InclusionInterface) => {
    const catCount = pkg.categories?.length || 0;

    if (catCount === 0) return "No categories yet";

    if (catCount === 1) return "1 category";

    return `${catCount} categories`;
  };

  const openCreateModal = () => {
    isEditPackageMode.value = false;
    packageForm.value = { name: "" };
    editingPackageId.value = null;
    showPackageModal.value = true;
  };

  const openEditModal = (pkg: InclusionInterface) => {
    isEditPackageMode.value = true;
    packageForm.value = { name: pkg.type };
    editingPackageId.value = pkg._id;
    showPackageModal.value = true;
  };

  const closePackageModal = () => {
    showPackageModal.value = false;
  };

  const savePackage = async () => {
    if (!packageForm.value.name.trim()) return;

    savingPackage.value = true;

    try {
      if (isEditPackageMode.value && editingPackageId.value) {
        await inclusionApi.updateInclusion(
          { type: packageForm.value.name },
          editingPackageId.value
        );
        toast.success("Package updated");
      } else {
        await inclusionApi.createInclusionForEstate({
          type: packageForm.value.name,
          estateId,
          inclusions: [],
          categories: [],
        });
        toast.success("Package created");
      }

      closePackageModal();
      await fetchPackages();
    } catch {
      toast.error("Failed to save package");
    } finally {
      savingPackage.value = false;
    }
  };

  const confirmDelete = async (pkg: InclusionInterface) => {
    if (!confirm(`Delete package "${pkg.type}"?`)) return;

    try {
      await inclusionApi.deleteInclusion(pkg._id);
      toast.success("Package deleted");
      await fetchPackages();
    } catch {
      toast.error("Failed to delete package");
    }
  };

  const openDetail = (pkg: InclusionInterface) => {
    selectedPackage.value = pkg;
    localCategories.value = pkg.categories ? [...pkg.categories] : [];
    collapsedCategories.value = new Set();
    view.value = "detail";
  };

  const toggleCategory = (catIdx: number) => {
    const next = new Set(collapsedCategories.value);

    if (next.has(catIdx)) {
      next.delete(catIdx);
    } else {
      next.add(catIdx);
    }

    collapsedCategories.value = next;
  };

  const openCreateCategoryModal = () => {
    isEditCategoryMode.value = false;
    editingCategoryIndex.value = null;
    categoryForm.value = { name: "" };
    showCategoryModal.value = true;
  };

  const openEditCategoryModal = (
    category: InclusionCategoryInterface,
    idx: number
  ) => {
    isEditCategoryMode.value = true;
    editingCategoryIndex.value = idx;
    categoryForm.value = { name: category.name };
    showCategoryModal.value = true;
  };

  const closeCategoryModal = () => {
    showCategoryModal.value = false;
  };

  const saveCategory = async () => {
    if (!categoryForm.value.name.trim() || !selectedPackage.value) return;

    savingCategory.value = true;

    try {
      const newCategories = [...localCategories.value];

      if (isEditCategoryMode.value && editingCategoryIndex.value !== null) {
        newCategories[editingCategoryIndex.value].name =
          categoryForm.value.name;
      } else {
        newCategories.push({
          name: categoryForm.value.name,
          items: [],
        });
      }

      await inclusionApi.updateInclusion(
        { categories: newCategories },
        selectedPackage.value._id
      );

      localCategories.value = newCategories;
      selectedPackage.value.categories = newCategories;
      const pkgIdx = packages.value.findIndex(
        (p) => p._id === selectedPackage.value!._id
      );

      if (pkgIdx !== -1) packages.value[pkgIdx].categories = newCategories;

      toast.success(
        isEditCategoryMode.value ? "Category updated" : "Category added"
      );
      closeCategoryModal();
    } catch {
      toast.error("Failed to save category");
    } finally {
      savingCategory.value = false;
    }
  };

  const confirmDeleteCategory = async (idx: number) => {
    const category = localCategories.value[idx];

    if (category.items.length > 0) {
      if (
        !confirm(
          `"${category.name}" has ${category.items.length} item(s). Delete anyway?`
        )
      )
        return;
    } else {
      if (!confirm(`Delete category "${category.name}"?`)) return;
    }

    if (!selectedPackage.value) return;

    const newCategories = localCategories.value.filter((_, i) => i !== idx);

    try {
      await inclusionApi.updateInclusion(
        { categories: newCategories },
        selectedPackage.value._id
      );
      localCategories.value = newCategories;
      selectedPackage.value.categories = newCategories;
      const pkgIdx = packages.value.findIndex(
        (p) => p._id === selectedPackage.value!._id
      );

      if (pkgIdx !== -1) packages.value[pkgIdx].categories = newCategories;

      toast.success("Category deleted");
    } catch {
      toast.error("Failed to delete category");
    }
  };

  const openCreateItemModal = (catIdx: number) => {
    isEditItemMode.value = false;
    editingCategoryIdx.value = catIdx;
    editingItemIndex.value = null;
    itemForm.value = { name: "", description: "", url: "" };
    itemNameError.value = false;
    showItemModal.value = true;
  };

  const openEditItemModal = (
    item: InclusionPackageInterface,
    catIdx: number,
    itemIdx: number
  ) => {
    isEditItemMode.value = true;
    editingCategoryIdx.value = catIdx;
    editingItemIndex.value = itemIdx;
    itemForm.value = {
      name: item.name,
      description: item.description.join("\n"),
      url: item.url || "",
    };
    itemNameError.value = false;
    showItemModal.value = true;
  };

  const closeItemModal = () => {
    showItemModal.value = false;
  };

  const saveItem = async () => {
    if (!itemForm.value.name.trim()) {
      itemNameError.value = true;
      return;
    }

    if (!selectedPackage.value || editingCategoryIdx.value === null) return;

    savingItem.value = true;

    try {
      const descLines = itemForm.value.description
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const updatedItem: InclusionPackageInterface = {
        name: itemForm.value.name,
        description: descLines,
        url: itemForm.value.url,
      };

      const newCategories = [...localCategories.value];
      const category = newCategories[editingCategoryIdx.value];

      if (isEditItemMode.value && editingItemIndex.value !== null) {
        category.items[editingItemIndex.value] = updatedItem;
      } else {
        category.items.push(updatedItem);
      }

      await inclusionApi.updateInclusion(
        { categories: newCategories },
        selectedPackage.value._id
      );

      localCategories.value = newCategories;
      selectedPackage.value.categories = newCategories;
      const pkgIdx = packages.value.findIndex(
        (p) => p._id === selectedPackage.value!._id
      );

      if (pkgIdx !== -1) packages.value[pkgIdx].categories = newCategories;

      toast.success(isEditItemMode.value ? "Item updated" : "Item added");
      closeItemModal();
    } catch {
      toast.error("Failed to save item");
    } finally {
      savingItem.value = false;
    }
  };

  const confirmDeleteItem = async (catIdx: number, itemIdx: number) => {
    if (!confirm("Delete this item?")) return;

    if (!selectedPackage.value) return;

    const newCategories = [...localCategories.value];

    newCategories[catIdx].items.splice(itemIdx, 1);

    try {
      await inclusionApi.updateInclusion(
        { categories: newCategories },
        selectedPackage.value._id
      );
      localCategories.value = newCategories;
      selectedPackage.value.categories = newCategories;
      const pkgIdx = packages.value.findIndex(
        (p) => p._id === selectedPackage.value!._id
      );

      if (pkgIdx !== -1) packages.value[pkgIdx].categories = newCategories;

      toast.success("Item deleted");
    } catch {
      toast.error("Failed to delete item");
    }
  };

const __returned__ = { route, toast, estateId, view, loading, packages, selectedPackage, localCategories, bulkUploading, bulkInputRef, handleBulkUpload, showPackageModal, isEditPackageMode, savingPackage, packageForm, editingPackageId, showCategoryModal, isEditCategoryMode, savingCategory, categoryForm, editingCategoryIndex, showItemModal, isEditItemMode, savingItem, editingCategoryIdx, editingItemIndex, itemNameError, itemForm, collapsedCategories, fetchPackages, getCategoryCountText, openCreateModal, openEditModal, closePackageModal, savePackage, confirmDelete, openDetail, toggleCategory, openCreateCategoryModal, openEditCategoryModal, closeCategoryModal, saveCategory, confirmDeleteCategory, openCreateItemModal, openEditItemModal, closeItemModal, saveItem, confirmDeleteItem, ref, onMounted, get useRoute() { return useRoute }, get useToast() { return useToast }, get inclusionApi() { return inclusionApi }, get InclusionInterface() { return InclusionInterface }, get InclusionPackageInterface() { return InclusionPackageInterface }, get InclusionCategoryInterface() { return InclusionCategoryInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})